import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { WorkflowTemplateService } from './workflow-template.service';
import { FormGroup, FormArray } from '@angular/forms';
import { formGroupPatchValueNullFriendly, castDates, initiateDownload } from 'app/helpers/utils';
import { finalize, tap, flatMap } from 'rxjs/operators';
import { Globals } from './globals';
import { HttpClient } from '@angular/common/http';
import { StepCompletionExecution } from 'app/models/step-completion-execution';
import { ToastService } from 'ng-uikit-pro-standard';
import { WorkflowExec } from 'app/models/workflow-exec';
import { UserService } from './user.service';
import { Resumable, ConfigurationHash } from 'resumablejs';
import { of } from 'rxjs';
import { PaginatedResponse } from 'app/models/paginated-response';
import { DateRange } from 'app/models/date-range';

@Injectable({
  providedIn: 'root'
})
export class WorkflowExecutionService {
  
  constructor(private http: HttpClient, private globals: Globals, private userService: UserService) {
    
  }

  submitExecution(workflowExec: WorkflowExec, stepCompletionExecution: StepCompletionExecution) {
    return this.http.post(`${this.globals.api_base_url}/workflow-template/${workflowExec.workflow_template_id}/execute`, {
      workflow_exec: workflowExec,
      step_completion_exec: stepCompletionExecution,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
  }

  getCurrentTask(workflowExecId: number, stepId: number) {
    var options:any = { 'workflow_step_id': stepId};
    return this.http.get<WorkflowExec>(`${this.globals.api_base_url}/workflow-execution/${workflowExecId}/current_task`, {params: options});
  }

  getCurrentTaskExternal(workflowExecId: number, stepId: number, contactId: number, token: string) {
    var options:any = { 'workflow_step_id': stepId, 'contact_id': contactId, 'token': token};
    return this.http.get<WorkflowExec>(`${this.globals.api_base_url}/external/workflow-execution/${workflowExecId}/current_task`, {params: options});
  }

  getMyTasksCount() {
    return this.userService.currentUser.pipe(flatMap((user) => {
      if (user && this.globals.api_base_url) {
        return this.http.get<{count: number}>(`${this.globals.api_base_url}/workflow-execution/my_tasks/count`, {});
      } else {
        return of({count: 0});
      }
    }));
  }
  
  getMyTasks() {
    return this.http.get<WorkflowExec[]>(`${this.globals.api_base_url}/workflow-execution/my_tasks`, {}).pipe(tap( (workflows) => {
      workflows.forEach(w => castDates(w, ['created_at', 'updated_at', 'due_on']));
    }));
  }

  downloadFile(workflowExecId: number, fileName: string) {
    fileName = encodeURIComponent(fileName);
    initiateDownload(`${this.globals.api_base_url}/workflow-execution/${workflowExecId}/download_file?file_name=${fileName}&bearer=${this.userService.access_token}`);
  }
  
  downloadAttachedFile(workflowExecId: number, workflow_file_name: string, step_completion_name: string) {
    workflow_file_name = encodeURIComponent(workflow_file_name);
    step_completion_name = encodeURIComponent(step_completion_name);
    initiateDownload(`${this.globals.api_base_url}/workflow-execution/${workflowExecId}/download_attached_file?workflow_file_name=${workflow_file_name}&step_completion_name=${step_completion_name}&bearer=${this.userService.access_token}`);
  }

  downloadInitialAttachedFile(workflowTemplateId: number, workflow_file_name: string) {
    workflow_file_name = encodeURIComponent(workflow_file_name);
    initiateDownload(`${this.globals.api_base_url}/workflow-template/${workflowTemplateId}/download_initial_attached_file?workflow_file_name=${workflow_file_name}&bearer=${this.userService.access_token}`);
  }

  completeStep(workflowExecId: number, stepCompletionExecution: StepCompletionExecution) {
    return this.http.post(`${this.globals.api_base_url}/workflow-execution/${workflowExecId}/complete_step`, {
      step_completion_exec: stepCompletionExecution,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
  }

  downloadFileExternal(workflowExecId: number, fileName: string, contactId: number, token: string) {
    fileName = encodeURIComponent(fileName);
    initiateDownload(`${this.globals.api_base_url}/external/workflow-execution/${workflowExecId}/download_file?file_name=${fileName}&contact_id=${contactId}&token=${token}`);
  }

  downloadAttachedFileExternal(workflowExecId: number, workflow_file_name: string, step_completion_name: string, contactId: number, token: string) {
    workflow_file_name = encodeURIComponent(workflow_file_name);
    step_completion_name = encodeURIComponent(step_completion_name);
    initiateDownload(`${this.globals.api_base_url}/external/workflow-execution/${workflowExecId}/download_file?workflow_file_name=${workflow_file_name}&step_completion_name=${step_completion_name}&contact_id=${contactId}&token=${token}`);
  }

  completeStepExternal(workflowExecId: number, stepCompletionExecution: StepCompletionExecution, contactId: number, token: string) {
    return this.http.post(`${this.globals.api_base_url}/external/workflow-execution/${workflowExecId}/complete_step`, {
      step_completion_exec: stepCompletionExecution,
      contact_id: contactId,
      token: token,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
  }

  list(pageNumber: number = 1, sortBy: string = 'created_at', sortDirection: string = 'desc',
        searchFilter: string = '', startDateFilter: DateRange = null,
        initiatorUserIds = [], initiatorTeamIds = [],
        targetUserIds = [], targetTeamIds = [], targetContactIds = [],
        filterInProgress: boolean = false, filterCompleted: boolean = false, filterTimedOut: boolean = false,
        filterDueBy: boolean = false, filterAllOverdue: boolean = false,
        inProgressDateFilter: DateRange = null, completedDateFilter: DateRange = null, timedOutDateFilter: DateRange = null) {
    return this.http.get<PaginatedResponse<WorkflowExec>>(`${this.globals.api_base_url}/workflow-execution`, {
      params: {
        "page": pageNumber.toString(),
        "sort_by": sortBy,
        "sort_dir": sortDirection,
        "search": searchFilter,
        "start_date": startDateFilter?.startDate?.toISOString() || '',
        "end_date": startDateFilter?.endDate?.toISOString() || '',
        "initiator_user_id[]": initiatorUserIds,
        "initiator_team_id[]": initiatorTeamIds,
        "target_user_id[]": targetUserIds,
        "target_team_id[]": targetTeamIds,
        "target_contact_id[]": targetContactIds,
        "filter_in_progress": filterInProgress? "1": "0",
        "filter_is_completed": filterCompleted? "1": "0",
        "filter_is_timed_out": filterTimedOut? "1": "0",
        "filter_due_by": filterDueBy? "1": "0",
        "filter_all_overdue": filterAllOverdue? "1": "0",
        "start_date_in_progress": inProgressDateFilter?.startDate?.toISOString() || '',
        "end_date_in_progress": inProgressDateFilter?.endDate?.toISOString() || '',
        "start_date_completed": completedDateFilter?.startDate?.toISOString() || '',
        "end_date_completed": completedDateFilter?.endDate?.toISOString() || '',
        "start_date_timed_out": timedOutDateFilter?.startDate?.toISOString() || '',
        "end_date_timed_out": timedOutDateFilter?.endDate?.toISOString() || ''
      }
    }).pipe(tap( (resp) => {
      resp.data.forEach(w => castDates(w, ['created_at', 'updated_at', 'due_on']));
    }));
  }

  getById(workflowExecId: number) {
    return this.http.get<WorkflowExec>(`${this.globals.api_base_url}/workflow-execution/${workflowExecId}`, {});
  }

  notifyCurrentStepTargets(workflowExecId: number) {
    return this.http.post(`${this.globals.api_base_url}/workflow-execution/${workflowExecId}/notify`, {});
  }
  
  
  getNewUploadTempFileResumable() {
    let r = new Resumable({
        target: `${this.globals.api_base_url}/workflow-execution/upload_file`,
        query: { bearer: this.userService.access_token },
        headers: { Authorization: `Bearer ${this.userService.access_token}` },
        maxChunkRetries: 2,
        simultaneousUploads: 1, // Laravel chunk misses merging some chunks when parallel uploading is turned on
        chunkSize: 1 * 1024 * 1024,
        testChunks: true,
        prioritizeFirstAndLastChunk: false, // Laravel chunk misses merging intermediate chunks when last is sent first
        xhrTimeout: 300000,
        minFileSize: 0 as any
    } as ConfigurationHash);

    this.userService.currentUser.subscribe(() => {
        r.opts.headers = { Authorization: `Bearer ${this.userService.access_token}` };
    });

    return r;
  }

  getNewUploadTempFileResumableExternal(workflowExecId: number, contactId: number, token: string) {
    let r = new Resumable({
        target: `${this.globals.api_base_url}/external/workflow-execution/${workflowExecId}/upload_file`,
        query: { 'contact_id': contactId, 'token': token },
        maxChunkRetries: 2,
        simultaneousUploads: 1, // Laravel chunk misses merging some chunks when parallel uploading is turned on
        chunkSize: 1 * 1024 * 1024,
        testChunks: true,
        prioritizeFirstAndLastChunk: false, // Laravel chunk misses merging intermediate chunks when last is sent first
        xhrTimeout: 300000,
        minFileSize: 0 as any
    } as ConfigurationHash);

    return r;
  }
}
